import React, { Component } from 'react';
import { observer, inject } from 'mobx-react';
import { observable } from 'mobx';
import { Alert, Button, Icon, Intent } from '@blueprintjs/core';
import { Api } from 'services/Api';
import Toast from 'helpers/toaster';
import moment from 'moment';
import VacationModal from 'components/User/VacationModal';

const EARLIER_SHOWN = 3; // past vacations shown before "Show N earlier"

const sentenceCase = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1).replace(/_/g, ' ') : '');

// A vacation's start and end as moments (raw dates, or the formatted ones).
const datesOf = (attributes) => ({
  start: moment(attributes.start_date || new Date(attributes.formatted_start_date)),
  end: moment(attributes.end_date || new Date(attributes.formatted_end_date)),
});

// "Oct 19 – 23", "Dec 22 – Jan 2", "Aug 4 – 8, 2025", "Dec 22, 2026 – Jan 2, 2027".
export const rangeLabel = (start, end, now = moment()) => {
  const thisYear = start.year() === now.year() && end.year() === now.year();
  if (start.isSame(end, 'day')) return start.format(thisYear ? 'MMM D' : 'MMM D, YYYY');
  if (start.year() !== end.year()) return `${start.format('MMM D, YYYY')} – ${end.format('MMM D, YYYY')}`;
  const year = thisYear ? '' : `, ${end.year()}`;
  if (start.isSame(end, 'month')) return `${start.format('MMM D')} – ${end.format('D')}${year}`;
  return `${start.format('MMM D')} – ${end.format('MMM D')}${year}`;
};

// "1 day, Fri", "5 days, Mon to Fri".
export const lengthLabel = (start, end) => {
  const days = moment(end).startOf('day').diff(moment(start).startOf('day'), 'days') + 1;
  if (days <= 1) return `1 day, ${start.format('ddd')}`;
  return `${days} days, ${start.format('ddd')} to ${end.format('ddd')}`;
};

// Away now, coming up (soonest first) and earlier (latest first).
export const groupVacations = (vacations, now = moment()) => {
  const groups = { now: [], upcoming: [], earlier: [] };
  vacations.forEach((vacation) => {
    const { start, end } = datesOf(vacation.attributes);
    const item = { vacation, start, end };
    if (end.isBefore(now)) groups.earlier.push(item);
    else if (start.isAfter(now)) groups.upcoming.push(item);
    else groups.now.push(item);
  });
  groups.upcoming.sort((a, b) => a.start - b.start);
  groups.earlier.sort((a, b) => b.start - a.start);
  return groups;
};

// A team member's vacations on their page (pages/users/show.js), as a small
// calendar: who's away now, what's coming up, and what's past. Add, edit and
// delete from here.
@inject('userStore') @observer
class UserVacations extends Component {
  @observable vacations = { isLoading: true, isError: false, objects: [] };
  @observable isModalOpen = false;
  @observable editing = null; // the vacation being edited, or null when adding
  @observable deleting = null; // the vacation waiting for delete confirmation
  @observable showAllEarlier = false;

  componentDidMount() {
    this.load();
  }

  load() {
    const { user } = this.props;
    this.vacations.isLoading = true;
    return Api.get(`/vacations/${user.attributes.id}`)
      .then((response) => {
        this.vacations = { isLoading: false, isError: false, objects: response.data.data || [] };
      })
      .catch(() => {
        this.vacations = { isLoading: false, isError: true, objects: [] };
      });
  }

  openAdd = () => {
    this.editing = null;
    this.props.userStore.vacation.object = { start_date: moment(), end_date: moment() };
    this.isModalOpen = true;
  };

  openEdit(vacation) {
    this.editing = vacation;
    this.props.userStore.vacation.object = vacation.attributes;
    this.isModalOpen = true;
  }

  // The panel closes after a save as well as on Cancel; reload either way.
  closeModal = () => {
    this.isModalOpen = false;
    this.editing = null;
    this.props.userStore.vacation.object = { start_date: moment(), end_date: moment() };
    this.load();
  };

  confirmDelete = () => {
    const vacation = this.deleting;
    this.deleting = null;
    this.props.userStore
      .deleteVacation(vacation)
      .then(() => {
        Toast.show({ message: 'Vacation deleted.', intent: Intent.SUCCESS });
        this.load();
      })
      .catch(() => {
        Toast.show({ message: "Couldn't delete the vacation. Try again.", intent: Intent.DANGER });
      });
  };

  // One line beside the title: when they're back, or when they're next away.
  summary(groups) {
    if (groups.now.length > 0) {
      const back = moment.max(groups.now.map((item) => item.end)).clone().add(1, 'day');
      return `Back ${back.format('MMM D')}`;
    }
    if (groups.upcoming.length > 0) return `Next away ${groups.upcoming[0].start.format('MMM D')}`;
    return 'None coming up';
  }

  renderRow({ vacation, start, end }) {
    const { status, created_by_fullname: by, notes } = vacation.attributes;
    const range = rangeLabel(start, end);
    return (
      <li key={vacation.id} className="uv-row">
        <span className="uv-leaf" aria-hidden="true">
          <span className="uv-leaf__month">{start.format('MMM')}</span>
          <span className="uv-leaf__day">{start.format('D')}</span>
        </span>
        <div className="uv-row__main">
          <span className="uv-row__range">{range}</span>
          <span className="uv-row__length">{lengthLabel(start, end)}</span>
          {(notes || by) && (
            <span className="uv-row__meta">
              {notes && <span className="uv-row__notes">{notes}</span>}
              {by && <span>Added by {by}</span>}
            </span>
          )}
        </div>
        {status && <span className={`up-pill up-pill--${status}`}>{sentenceCase(status)}</span>}
        <span className="uv-row__actions">
          <button type="button" className="up-icon-button" aria-label={`Edit vacation ${range}`} onClick={() => this.openEdit(vacation)}>
            <Icon icon="edit" iconSize={14} />
          </button>
          <button type="button" className="up-icon-button up-icon-button--danger" aria-label={`Delete vacation ${range}`} onClick={() => { this.deleting = vacation; }}>
            <Icon icon="trash" iconSize={14} />
          </button>
        </span>
      </li>
    );
  }

  renderGroup(key, title, items, note) {
    if (items.length === 0) return null;
    return (
      <section className={`uv-group uv-group--${key}`} aria-labelledby={`uv-${key}`}>
        <h3 className="uv-group__title" id={`uv-${key}`}>
          {title}
          {note && <span className="uv-group__note">{note}</span>}
        </h3>
        <ul className="uv-list">{items.map((item) => this.renderRow(item))}</ul>
      </section>
    );
  }

  renderBody(groups) {
    const { isLoading, isError, objects } = this.vacations;
    const firstName = this.props.user.attributes.first_name || 'They';
    if (isLoading && objects.length === 0) {
      return (
        <ul className="uv-list" aria-hidden="true">
          {[0, 1].map((index) => (
            <li key={index} className="uv-row uv-row--loading">
              <span className="dl-bone uv-leaf" />
              <span className="dl-bone" style={{ width: 160, height: 14 }} />
            </li>
          ))}
        </ul>
      );
    }
    if (isError) {
      return (
        <p className="up-empty" role="alert">
          Vacations couldn't be loaded.{' '}
          <button type="button" className="up-text-button" onClick={() => this.load()}>Try again</button>
        </p>
      );
    }
    if (objects.length === 0) {
      return <p className="up-empty">{`${firstName} has no vacations booked. Add one so they aren't scheduled while away.`}</p>;
    }
    const earlier = this.showAllEarlier ? groups.earlier : groups.earlier.slice(0, EARLIER_SHOWN);
    const hidden = groups.earlier.length - earlier.length;
    return (
      <React.Fragment>
        {this.renderGroup('now', 'Away now', groups.now)}
        {this.renderGroup('upcoming', 'Coming up', groups.upcoming)}
        {this.renderGroup('earlier', 'Earlier', earlier)}
        {hidden > 0 && (
          <button type="button" className="up-text-button uv-more" onClick={() => { this.showAllEarlier = true; }}>
            {`Show ${hidden} earlier`}
          </button>
        )}
      </React.Fragment>
    );
  }

  render() {
    const { user } = this.props;
    const groups = groupVacations(this.vacations.objects);
    const deleting = this.deleting && this.deleting.attributes;
    const showSummary = !this.vacations.isLoading && !this.vacations.isError && this.vacations.objects.length > 0;
    return (
      <section className="up-card uv" aria-labelledby="up-vacations-title">
        <header className="up-card__head">
          <div className="uv-head">
            <h2 className="up-card__title" id="up-vacations-title">Vacations</h2>
            {showSummary && <span className={`uv-summary${groups.now.length ? ' is-away' : ''}`}>{this.summary(groups)}</span>}
          </div>
          <Button className="up-button" icon="add" text="Add vacation" onClick={this.openAdd} />
        </header>
        {this.renderBody(groups)}

        <VacationModal
          isOpen={this.isModalOpen}
          onClose={this.closeModal}
          user={user}
          vacation={this.editing ? this.editing.attributes : null}
        />
        <Alert
          isOpen={Boolean(this.deleting)}
          icon="trash"
          intent={Intent.DANGER}
          cancelButtonText="Cancel"
          confirmButtonText="Delete vacation"
          onCancel={() => { this.deleting = null; }}
          onConfirm={this.confirmDelete}
        >
          <strong>Delete this vacation?</strong>
          {deleting && (
            <p>{`${rangeLabel(datesOf(deleting).start, datesOf(deleting).end)} comes off ${user.attributes.first_name ? `${user.attributes.first_name}'s` : 'their'} calendar.`}</p>
          )}
        </Alert>
      </section>
    );
  }
}

export default UserVacations;
